<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  flowSteps: { type: Array, required: true },
  stepIndex: { type: Number, required: true },
});

defineEmits(['close', 'remove', 'addOption', 'removeOption']);

const { t } = useI18n();

const step = computed(() => props.flowSteps[props.stepIndex]);

const STEP_TYPES = ['message', 'question', 'decision', 'prayer', 'end'];

const STEP_BADGE = {
  message: 'bg-blue-100 text-blue-700',
  question: 'bg-purple-100 text-purple-700',
  decision: 'bg-yellow-100 text-yellow-700',
  prayer: 'bg-pink-100 text-pink-700',
  end: 'bg-green-100 text-green-700',
};

function stepLabel(s) {
  const txt = s.message_text || '';
  const preview = txt.length > 40 ? `${txt.slice(0, 40)}…` : txt;
  return `${t('MINISTRY.FLOW_BUILDER.STEP_PREFIX')} ${s.step_number}${preview ? ` — ${preview}` : ''}`;
}
</script>

<template>
  <div
    class="flex flex-col h-full border-l border-gray-200 bg-white w-80 shrink-0"
  >
    <!-- Panel header -->
    <div
      class="flex items-center justify-between px-4 py-3 border-b border-gray-200 shrink-0"
    >
      <div class="flex items-center gap-2">
        <span class="text-sm font-semibold text-gray-700">
          {{ t('MINISTRY.FLOW_BUILDER.STEP_PREFIX') }} {{ step.step_number }}
        </span>
        <span
          class="text-[10px] font-semibold px-2 py-0.5 rounded-full"
          :class="STEP_BADGE[step.step_type] || STEP_BADGE.message"
        >
          {{ step.step_type }}
        </span>
      </div>
      <button
        class="p-1.5 rounded hover:bg-gray-100 text-gray-400 hover:text-gray-700 transition-colors"
        @click="$emit('close')"
      >
        <span class="i-lucide-x size-4" />
      </button>
    </div>

    <!-- Fields -->
    <div class="flex-1 min-h-0 overflow-y-auto px-4 py-4 space-y-5">
      <!-- Type + Delay row -->
      <div class="flex items-end gap-3">
        <div class="flex-1">
          <label
            class="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1"
          >
            {{ t('MINISTRY.FLOW_BUILDER.TYPE_LABEL') }}
          </label>
          <select
            v-model="step.step_type"
            class="w-full text-sm px-2.5 py-1.5 rounded-lg border border-gray-200 bg-white text-gray-700 focus:outline-none focus:border-woot-400"
          >
            <option v-for="st in STEP_TYPES" :key="st" :value="st">
              {{ st }}
            </option>
          </select>
        </div>
        <div class="w-20">
          <label
            class="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1"
          >
            {{ t('MINISTRY.FLOW_BUILDER.DELAY_LABEL') }}
          </label>
          <input
            v-model.number="step.delay_seconds"
            type="number"
            min="0"
            class="w-full text-sm px-2.5 py-1.5 rounded-lg border border-gray-200 bg-white text-center focus:outline-none focus:border-woot-400"
          />
        </div>
      </div>

      <!-- Message text -->
      <div>
        <label
          class="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1"
        >
          {{ t('MINISTRY.FLOW_BUILDER.MESSAGE_LABEL') }}
        </label>
        <textarea
          v-model="step.message_text"
          rows="6"
          :placeholder="t('MINISTRY.FLOW_BUILDER.MESSAGE_PLACEHOLDER')"
          class="block w-full text-sm px-3 py-2 rounded-lg border border-gray-200 bg-white text-gray-800 placeholder-gray-300 focus:outline-none focus:border-woot-400 resize-y"
        />
      </div>

      <!-- Response options -->
      <div>
        <label
          class="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2"
        >
          {{ t('MINISTRY.FLOW_BUILDER.RESPONSE_OPTIONS') }}
        </label>
        <div class="space-y-3">
          <div
            v-for="(opt, oi) in step.options"
            :key="opt.id ?? oi"
            class="rounded-lg border border-gray-200 bg-gray-50 p-3 space-y-2"
          >
            <!-- Option text -->
            <div class="flex items-center gap-2">
              <span
                class="shrink-0 w-5 h-5 rounded-full bg-gray-200 text-gray-600 text-[10px] font-bold flex items-center justify-center"
              >
                {{ opt.option_number }}
              </span>
              <input
                v-model="opt.option_text"
                type="text"
                :placeholder="t('MINISTRY.FLOW_BUILDER.OPTION_PLACEHOLDER')"
                class="flex-1 text-sm px-2.5 py-1.5 rounded-md border border-gray-200 bg-white text-gray-800 placeholder-gray-300 focus:outline-none focus:border-woot-400"
              />
              <button
                class="shrink-0 p-1 rounded hover:bg-red-50 text-gray-400 hover:text-red-500 transition-colors"
                :aria-label="t('MINISTRY.FLOW_BUILDER.REMOVE_OPTION')"
                @click="$emit('removeOption', oi)"
              >
                <span class="i-lucide-x size-3.5" />
              </button>
            </div>

            <!-- Routing + tag + notify -->
            <div class="pl-7 space-y-1.5">
              <div class="flex items-center gap-1.5">
                <span class="text-xs text-gray-500 shrink-0">
                  {{ t('MINISTRY.FLOW_BUILDER.GO_TO') }}
                </span>
                <select
                  v-model="opt.next_step_id"
                  class="flex-1 text-xs px-2 py-1 rounded-md border border-gray-200 bg-white text-gray-700 focus:outline-none focus:border-woot-400"
                >
                  <option :value="null">
                    {{ t('MINISTRY.FLOW_BUILDER.END_FLOW') }}
                  </option>
                  <option
                    v-for="(s, idx) in flowSteps"
                    :key="idx"
                    :value="s.id ?? s.tempKey"
                    :disabled="idx === stepIndex"
                  >
                    {{ stepLabel(s) }}
                  </option>
                </select>
              </div>
              <div class="flex items-center gap-3 flex-wrap">
                <div class="flex items-center gap-1.5">
                  <span class="text-xs text-gray-500">
                    {{ t('MINISTRY.FLOW_BUILDER.TAG_LABEL') }}
                  </span>
                  <input
                    v-model="opt.tag_to_apply"
                    type="text"
                    :placeholder="t('MINISTRY.FLOW_BUILDER.TAG_PLACEHOLDER')"
                    class="w-24 text-xs px-2 py-1 rounded-md border border-gray-200 bg-white text-gray-700 placeholder-gray-300 focus:outline-none focus:border-woot-400"
                  />
                </div>
                <label
                  class="flex items-center gap-1.5 text-xs text-gray-500 cursor-pointer"
                >
                  <input
                    v-model="opt.notify_agent"
                    type="checkbox"
                    class="rounded"
                  />
                  {{ t('MINISTRY.FLOW_BUILDER.NOTIFY_AGENT') }}
                </label>
              </div>
            </div>
          </div>
        </div>

        <button
          class="mt-2 text-sm text-woot-500 hover:text-woot-600 font-medium flex items-center gap-1.5 transition-colors"
          @click="$emit('addOption')"
        >
          <span class="i-lucide-plus size-4" />
          {{ t('MINISTRY.FLOW_BUILDER.ADD_OPTION') }}
        </button>
      </div>
    </div>

    <!-- Delete step footer -->
    <div class="px-4 py-3 border-t border-gray-200 shrink-0">
      <button
        class="w-full py-2 rounded-lg border border-red-200 text-red-500 hover:bg-red-50 text-sm font-medium transition-colors flex items-center justify-center gap-2"
        @click="$emit('remove')"
      >
        <span class="i-lucide-trash-2 size-4" />
        {{ t('MINISTRY.FLOW_BUILDER.DELETE_STEP') }}
      </button>
    </div>
  </div>
</template>
